import React, { useEffect, useRef, useState } from 'react';
import siteAnalyticsService, { RetentionResponse } from '../../services/siteAnalytics.service';
import { LoadError, fa } from './useDateRange';
import './analytics.css';

/** کلید cohort = شنبهٔ شروع هفته (تهران) به شکل YYYY-MM-DD → نمایش جلالی */
const fmtCohort = (cohort: string): string => {
  try {
    // T12:00:00 → در مرورگرهای با آفست منفی یک روز عقب نمی‌افتد
    return new Intl.DateTimeFormat('fa-IR', { dateStyle: 'short' }).format(new Date(cohort + 'T12:00:00'));
  } catch {
    return cohort;
  }
};

const SiteAnalyticsRetention: React.FC = () => {
  const [weeks, setWeeks] = useState(8);
  const [data, setData] = useState<RetentionResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const reqIdRef = useRef(0);
  const [activity, setActivity] = useState<{ DAU: number; WAU: number; MAU: number; stickinessPct: number } | null>(
    null
  );

  const load = async () => {
    const reqId = ++reqIdRef.current;
    setLoading(true);
    setErr(null);
    try {
      const [retention, act] = await Promise.all([
        siteAnalyticsService.getRetention(weeks),
        // پرمیشن جدا (admin_analytics_activity) دارد؛ 403 → فقط کارت‌های DAU/WAU پنهان می‌شوند.
        siteAnalyticsService.getActivityStats({ silentForbidden: true }).catch(() => null),
      ]);
      if (reqId !== reqIdRef.current) return;
      setData(retention);
      setActivity(act);
    } catch (e: any) {
      if (reqId !== reqIdRef.current) return;
      setErr(e?.message || 'خطا در دریافت داده');
    } finally {
      if (reqId === reqIdRef.current) setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [weeks]);

  const heatmapClass = (pct: number | null): string => {
    if (pct === null) return 'c0';
    if (pct >= 60) return 'c6';
    if (pct >= 40) return 'c5';
    if (pct >= 25) return 'c4';
    if (pct >= 15) return 'c3';
    if (pct >= 8) return 'c2';
    if (pct >= 3) return 'c1';
    return 'c0';
  };

  // 🆕 میانگین وزنی بازگشت هر هفته روی همه‌ی cohortها — منحنی کلی week-N
  // (سلول‌های «هنوز نرسیده» = pct:null در مخرج شمرده نمی‌شوند)
  const weightedAvg = (weekIdx: number): number | null => {
    if (!data) return null;
    let num = 0;
    let den = 0;
    for (const c of data.cohorts) {
      const cell = c.retention[weekIdx];
      if (!cell || cell.pct === null) continue;
      num += cell.visitors;
      den += c.size;
    }
    return den ? Math.round((num / den) * 100) : null;
  };

  return (
    <div className="analytics-page">
      <div className="analytics-header">
        <h2>تحلیل بازگشت کاربران (Retention)</h2>
        <div className="analytics-controls">
          <select value={weeks} onChange={(e) => setWeeks(parseInt(e.target.value, 10))}>
            <option value={4}>۴ هفته</option>
            <option value={8}>۸ هفته</option>
            <option value={12}>۱۲ هفته</option>
            <option value={24}>۲۴ هفته</option>
          </select>
          <button onClick={load} disabled={loading}>
            {loading ? 'در حال بارگذاری…' : 'بروزرسانی'}
          </button>
        </div>
      </div>

      {err && <LoadError message={err} onRetry={load} />}

      {activity && (
        <div className="analytics-kpi-grid">
          <div className="analytics-kpi-card">
            <div className="accent" />
            <div className="label">DAU</div>
            <div className="value">{fa(activity.DAU)}</div>
          </div>
          <div className="analytics-kpi-card green">
            <div className="accent" />
            <div className="label">WAU</div>
            <div className="value">{fa(activity.WAU)}</div>
          </div>
          <div className="analytics-kpi-card purple">
            <div className="accent" />
            <div className="label">MAU</div>
            <div className="value">{fa(activity.MAU)}</div>
          </div>
          <div className="analytics-kpi-card orange">
            <div className="accent" />
            <div className="label">Stickiness</div>
            <div className="value">{fa(activity.stickinessPct)}٪</div>
            <span className="change flat">DAU/MAU</span>
          </div>
        </div>
      )}

      <div className="analytics-section">
        <div className="analytics-section-header">
          <h3>Cohort Retention (هفته‌به‌هفته)</h3>
        </div>
        {loading && !data ? (
          <div className="analytics-loading">در حال بارگذاری…</div>
        ) : !data || data.cohorts.length === 0 ? (
          <div className="analytics-empty">داده‌ای برای cohort وجود ندارد</div>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table className="analytics-table">
              <thead>
                <tr>
                  <th>Cohort (شروع)</th>
                  <th>اندازه</th>
                  {Array.from({ length: weeks + 1 }, (_, i) => (
                    <th key={i} className="analytics-cohort-cell" style={{ minWidth: 60 }}>
                      هفته {fa(i)}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {data.cohorts.map((c) => (
                  <tr key={c.cohort}>
                    <td style={{ whiteSpace: 'nowrap' }} title={c.cohort}>
                      هفتهٔ {fmtCohort(c.cohort)}
                    </td>
                    <td>
                      <strong>{fa(c.size)}</strong>
                    </td>
                    {c.retention.map((r) => (
                      <td
                        key={r.week}
                        className={`analytics-cohort-cell ${heatmapClass(r.pct)}`}
                        title={r.pct === null ? 'این هفته هنوز برای این cohort نرسیده' : `${r.visitors} بازدیدکننده`}
                      >
                        {r.pct === null ? '·' : r.visitors > 0 ? `${fa(r.pct)}٪` : '—'}
                      </td>
                    ))}
                  </tr>
                ))}
                {/* 🆕 میانگین وزنی کل — منحنی بازگشت سراسری */}
                <tr style={{ borderTop: '2px solid var(--color-border)' }}>
                  <td style={{ fontWeight: 700 }}>میانگین کل (وزنی)</td>
                  <td>
                    <strong>{fa(data.cohorts.reduce((s, c) => s + c.size, 0))}</strong>
                  </td>
                  {Array.from({ length: weeks + 1 }, (_, i) => {
                    const avg = weightedAvg(i);
                    return (
                      <td key={i} className={`analytics-cohort-cell ${heatmapClass(avg)}`} style={{ fontWeight: 700 }}>
                        {avg === null ? '·' : `${fa(avg)}٪`}
                      </td>
                    );
                  })}
                </tr>
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default SiteAnalyticsRetention;
